import { MemberName, RemoveXgSeatList } from "@components/table/members/remove-xg-seat/remove-xg-seat-list";
import { getTeamRemoveBlockReason, getTeamsToLeave } from "@components/table/members/remove-xg-seat/remove-xg-seat-utils";
import { useMemberTeams } from "@components/table/members/remove-xg-seat/use-member-teams";
import { usePendingIds } from "@components/table/members/remove-xg-seat/use-pending-ids";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useAppDispatch } from "@store/store-helper";
import { removeMembersFromTeam } from "@store/teams/teams-slice-thunk";
import { formatUserRoleType } from "@utils/data-display";
/**
 * Step of taking a Sphere XG seat away from a member that lists the teams the member has to leave
 * first, see getTeamsToLeave, and lets the user remove the member from them. The teams are fetched
 * again whenever the step is shown.
 */
export function RemoveXgSeatTeamsStep({
  member,
  seatKind,
  onBusyChange,
  onResolvedChange
}) {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    teams,
    status
  } = useMemberTeams(member.identity);
  const {
    pendingIds,
    runWhilePending
  } = usePendingIds(onBusyChange);
  const editorTeams = getTeamsToLeave(teams, seatKind);
  const name = <MemberName member={member} />;
  return <RemoveXgSeatList status={status} description={seatKind === "assigned" ? <>
            These teams give {name} a workspace role that needs a Sphere XG
            seat. Remove {name} from them.
          </> : <>
            {name} keeps a Sphere XG seat as long as they belong to these teams.
            Remove {name} from them.
          </>} emptyMessage={seatKind === "assigned" ? <>{name} does not belong to any team with an editor role.</> : <>{name} does not belong to any team.</>} failedMessage="The teams of the member could not be loaded." items={editorTeams.map(toItem)} pendingIds={pendingIds} onResolvedChange={onResolvedChange} bulkActions={[{
    label: "Remove from all teams",
    variant: "destructiveGhost",
    onClick: () => void removeFromTeams(editorTeams.filter(team => !getTeamRemoveBlockReason(team)))
  }]} />;

  /** Returns how a team is listed */
  function toItem(team_0) {
    return {
      id: team_0.id,
      name: team_0.name,
      details: `Workspace role: ${formatUserRoleType(team_0.role)}`,
      blockReason: getTeamRemoveBlockReason(team_0),
      actions: [{
        label: "Remove",
        variant: "destructiveGhost",
        onClick: () => void removeFromTeams([team_0])
      }]
    };
  }

  /**
   * Removes the member from the teams, which drops them from the list. A team the member could not
   * be removed from stays listed, and the user is told why by the error toast of the request.
   */
  async function removeFromTeams(teamsToLeave) {
    await runWhilePending(teamsToLeave.map(({
      id
    }) => id), () => Promise.allSettled(teamsToLeave.map(team_1 => dispatch(removeMembersFromTeam({
      companyId,
      teamId: team_1.id,
      memberIds: [member.identity]
    })).unwrap())));
  }
}